import { useEffect, useState } from "react";
import { Sparkles, X, Heart } from "lucide-react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canSeeAffiliateUI } from "../lib/permissions";

const DISMISS_KEY = "affiliate_banner_dismissed_ref";

/**
 * Site-wide banner that shows the active affiliate on EVERY page. Gives
 * customers continuous visibility into who will be credited — and makes
 * accidental re-shares of the URL less likely to dilute the original
 * affiliate's credit.
 *
 * Dismissible per affiliate slug (clicking X hides it for THIS ref only;
 * if a new affiliate gets captured, the banner re-appears).
 */
export default function AffiliateBanner() {
  const [active, setActive] = useState(null);
  const [ownerName, setOwnerName] = useState(null);
  const [dismissed, setDismissed] = useState(null);
  const location = useLocation();
  const { user } = useAuth();
  const showFullCommissionCopy = canSeeAffiliateUI(user);

  // Re-fetch on every route change so the banner stays in sync when the user
  // clicks a fresh affiliate link mid-session. We ONLY surface LOCAL
  // affiliates (users who actually exist on guyanadiscounts.com) so the
  // banner never falsely promises a commission for an external/orphan slug.
  useEffect(() => {
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const { data } = await api.get("/session/affiliate");
        if (cancelled) return;
        if (data.active && data.is_local) {
          setActive(data.active);
          setOwnerName(data.local_owner_name || null);
        } else {
          setActive(null);
          setOwnerName(null);
        }
      } catch {
        if (!cancelled) {
          setActive(null);
          setOwnerName(null);
        }
      }
    };
    fetchActive();
    // Also re-fetch when `_captureLandingParams` finishes rewriting the ref
    // — this catches the mount-time race where the banner rendered before the
    // fresh cookie was persisted. Fires on affiliate-ref-updated events.
    const onRefUpdated = () => { fetchActive(); };
    window.addEventListener("affiliate-ref-updated", onRefUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener("affiliate-ref-updated", onRefUpdated);
    };
  }, [location.pathname]);

  useEffect(() => {
    try { setDismissed(localStorage.getItem(DISMISS_KEY) || null); } catch { /* ignore */ }
  }, []);

  const dismiss = () => {
    if (!active) return;
    try { localStorage.setItem(DISMISS_KEY, active); } catch { /* ignore */ }
    setDismissed(active);
  };

  if (!active || dismissed === active) return null;

  // Customer view: subtle "Supporting: X" chip. No mention of commissions.
  if (!showFullCommissionCopy) {
    return (
      <div
        data-testid="affiliate-banner"
        className="bg-neutral-900 text-white text-[11px] sm:text-xs relative w-full overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 py-1.5 flex items-center justify-center gap-2">
          <Heart className="w-3 h-3 shrink-0 text-[#FF2A00] fill-[#FF2A00]" />
          <span className="text-center min-w-0 leading-snug">
            Supporting{" "}
            {ownerName ? (
              <strong className="font-semibold" data-testid="affiliate-banner-ref">{ownerName}</strong>
            ) : (
              <code data-testid="affiliate-banner-ref" className="font-mono">{active}</code>
            )}
          </span>
          <button
            data-testid="affiliate-banner-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="shrink-0 p-1 hover:bg-white/10 rounded"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  // Seller / admin view: full commission copy.
  return (
    <div
      data-testid="affiliate-banner"
      className="bg-[#0033FF] text-white text-[11px] sm:text-sm relative w-full overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 py-2 flex items-start sm:items-center justify-center gap-2 sm:gap-3">
        <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5 sm:mt-0" />
        <span className="text-center min-w-0 flex-1 leading-snug break-words">
          You're shopping via{" "}
          {ownerName && <strong className="font-semibold">{ownerName}</strong>}
          {ownerName && " — "}
          <code
            data-testid="affiliate-banner-ref"
            className="font-mono font-bold bg-white/15 px-1.5 py-0.5 break-all"
          >
            {active}
          </code>
          {" "}— they'll earn a commission on your order. Thanks for supporting them.
        </span>
        <button
          data-testid="affiliate-banner-dismiss"
          onClick={dismiss}
          aria-label="Dismiss banner"
          className="shrink-0 p-1 hover:bg-white/20 rounded"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
